/**
 * 100 Years of the Stock Market.
 *
 * Headline figures, a metric switcher, a Chart.js line across the decades, and
 * the century's major market events as a timeline.
 *
 * The data URL arrives through wp_localize_script (stMarketHistory.dataUrl).
 */
(function () {
	"use strict";

	var CONFIG = window.buzzMarketHistory || {};

	// Reads the theme palette (Customizer CSS variables) off :root so the chart cannot drift from the
	// rest of the page when a token changes.
	function token(name, fallback) {
		var v = getComputedStyle(document.documentElement).getPropertyValue(name);
		return (v && v.trim()) || fallback;
	}

	var METRICS = [
		{ key: "sp500_return", label: "S&P 500 Return", suffix: "%" },
		{ key: "inflation", label: "Inflation", suffix: "%" },
		{ key: "gdp_growth", label: "GDP Growth", suffix: "%" },
		{ key: "growth", label: "$100 Invested", prefix: "$" }
	];

	function num(n) {
		return Number(n).toLocaleString("en-US");
	}

	/** The headline figures above the chart. */
	function stats(data) {
		var decades = data.decades || [];
		var growth = data.growth || [];

		var avg =
			decades.reduce(function (sum, d) {
				return sum + (d.sp500_return || 0);
			}, 0) / (decades.length || 1);

		var best = decades.reduce(function (a, b) {
			return (b.sp500_return || 0) > (a.sp500_return || 0) ? b : a;
		}, decades[0] || {});

		var worst = decades.reduce(function (a, b) {
			return (b.sp500_return || 0) < (a.sp500_return || 0) ? b : a;
		}, decades[0] || {});

		var first = growth[0];
		var last = growth[growth.length - 1];
		var multiple = first && last ? Math.round(last.value / first.value) : 0;

		return [
			{ value: avg.toFixed(1) + "%", label: "Avg annual return" },
			{ value: best.decade || "—", label: "Best decade" },
			{ value: worst.decade || "—", label: "Worst decade" },
			{ value: multiple ? multiple + "×" : "—", label: "$100 grew to" }
		];
	}

	function renderStats(host, data) {
		var wrap = document.createElement("div");
		wrap.className = "market-history__stats";

		stats(data).forEach(function (s) {
			var cell = document.createElement("div");
			cell.className = "market-stat";

			var v = document.createElement("span");
			v.className = "market-stat__value";
			v.textContent = s.value;

			var l = document.createElement("span");
			l.className = "market-stat__label";
			l.textContent = s.label;

			cell.appendChild(v);
			cell.appendChild(l);
			wrap.appendChild(cell);
		});

		host.appendChild(wrap);
	}

	function renderEvents(host, data) {
		var events = data.events || [];

		if (!events.length) return;

		var head = document.createElement("h3");
		head.className = "market-history__title";
		head.style.fontSize = "1.4rem";
		head.textContent = "Events that moved the market";

		var wrap = document.createElement("div");
		wrap.className = "market-history__events";

		events.forEach(function (e) {
			var down = String(e.impact).charAt(0) === "-";

			var row = document.createElement("article");
			row.className = "market-event market-event--" + (down ? "crash" : "boom");

			var year = document.createElement("span");
			year.className = "market-event__year";
			year.textContent = e.year;

			var body = document.createElement("div");

			var title = document.createElement("h4");
			title.className = "market-event__title";
			title.textContent = e.event;

			var text = document.createElement("p");
			text.className = "market-event__text";
			text.textContent = e.description;

			var change = document.createElement("span");
			change.className = "market-event__change market-event__change--" + (down ? "neg" : "pos");
			change.textContent = e.impact;

			body.appendChild(title);
			body.appendChild(text);
			body.appendChild(change);

			row.appendChild(year);
			row.appendChild(body);
			wrap.appendChild(row);
		});

		host.appendChild(head);
		host.appendChild(wrap);
	}

	function renderChart(host, data) {
		var controls = document.createElement("div");
		controls.className = "market-history__controls";

		var plate = document.createElement("div");
		plate.className = "market-history__chart";

		var canvas = document.createElement("canvas");
		plate.appendChild(canvas);

		host.appendChild(controls);
		host.appendChild(plate);

		// Chart.js is loaded from a CDN; without it the figures and the events
		// timeline still render, so the page stays useful.
		if (typeof window.Chart === "undefined") {
			plate.innerHTML =
				'<p class="market-history__disclaimer">The chart could not be loaded.</p>';
			return;
		}

		var accent = token("--primary-color", "");
		var ink = token("--text-muted", "");
		var rule = token("--border-color", "");

		var chart = null;

		function seriesFor(metric) {
			if (metric.key === "growth") {
				var g = data.growth || [];
				return {
					labels: g.map(function (p) {
						return p.year;
					}),
					values: g.map(function (p) {
						return p.value;
					})
				};
			}

			var d = data.decades || [];
			return {
				labels: d.map(function (p) {
					return p.decade;
				}),
				values: d.map(function (p) {
					return p[metric.key];
				})
			};
		}

		function draw(metric) {
			var s = seriesFor(metric);

			if (chart) chart.destroy();

			chart = new window.Chart(canvas, {
				type: "line",
				data: {
					labels: s.labels,
					datasets: [
						{
							label: metric.label,
							data: s.values,
							borderColor: accent,
							backgroundColor: /^#[0-9a-f]{6}$/i.test(accent) ? accent + "1A" : accent,
							borderWidth: 2,
							pointRadius: 3,
							pointBackgroundColor: accent,
							fill: true,
							tension: 0.28
						}
					]
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: { display: false },
						tooltip: {
							callbacks: {
								label: function (ctx) {
									var v = ctx.parsed.y;
									return (
										metric.label +
										": " +
										(metric.prefix || "") +
										num(v) +
										(metric.suffix || "")
									);
								}
							}
						}
					},
					scales: {
						x: {
							grid: { color: rule },
							ticks: { color: ink, font: { size: 11 } }
						},
						y: {
							// A logarithmic scale for the $100 series: on a linear
							// axis a century of compounding flattens everything
							// before 1990 into the baseline.
							type: metric.key === "growth" ? "logarithmic" : "linear",
							grid: { color: rule },
							ticks: {
								color: ink,
								font: { size: 11 },
								callback: function (v) {
									return (metric.prefix || "") + num(v) + (metric.suffix || "");
								}
							}
						}
					}
				}
			});
		}

		METRICS.forEach(function (metric, i) {
			var btn = document.createElement("button");
			btn.type = "button";
			btn.className = "market-history__btn" + (i === 0 ? " is-active" : "");
			btn.textContent = metric.label;

			btn.addEventListener("click", function () {
				controls.querySelectorAll(".market-history__btn").forEach(function (b) {
					b.classList.remove("is-active");
				});
				btn.classList.add("is-active");
				draw(metric);
			});

			controls.appendChild(btn);
		});

		draw(METRICS[0]);
	}

	function build(host, data) {
		host.innerHTML = "";

		var title = host.getAttribute("data-title");
		var subtitle = host.getAttribute("data-subtitle");

		if (title || subtitle) {
			var head = document.createElement("header");
			head.className = "market-history__header";

			if (title) {
				var h = document.createElement("h2");
				h.className = "market-history__title";
				h.textContent = title;
				head.appendChild(h);
			}

			if (subtitle) {
				var p = document.createElement("p");
				p.className = "market-history__subtitle";
				p.textContent = subtitle;
				head.appendChild(p);
			}

			host.appendChild(head);
		}

		renderStats(host, data);
		renderChart(host, data);
		renderEvents(host, data);

		var note = document.createElement("p");
		note.className = "market-history__disclaimer";
		note.textContent =
			"Past performance doesn't guarantee future results. Figures are decade averages.";
		host.appendChild(note);
	}

	function load(host) {
		var url = CONFIG.dataUrl;

		if (!url) {
			host.innerHTML =
				'<p class="market-history__disclaimer">The dashboard data could not be located.</p>';
			return;
		}

		fetch(url)
			.then(function (r) {
				if (!r.ok) throw new Error("HTTP " + r.status);
				return r.json();
			})
			.then(function (data) {
				build(host, data);
			})
			.catch(function () {
				host.innerHTML =
					'<p class="market-history__disclaimer">The dashboard data could not be loaded.</p>';
			});
	}

	function init() {
		var hosts = document.querySelectorAll(".market-history-dashboard");

		Array.prototype.forEach.call(hosts, load);
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
